import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";

import { PageContent } from "../../../../../WorkerShifts/components/PageContent";
import { useCreateProfessionalReference } from "../api/useCreateProfessionalReference";
import { invalidateProfessionalReferences } from "../api/useGetProfessionalReferences";
import { FORM_FIELDS, KNOWN_CREATE_REFERENCE_USER_ERROR_MESSAGES } from "../constants";
import { type ReferenceFormData } from "../types";
import { getErrorMessage } from "../utils/errors";
import { transformFormDataToRequest } from "../utils/formatters";
import { referenceFormSchema } from "../utils/validation";
import { EmploymentDateFields } from "./EmploymentDateFields";
import { InputFormField } from "./InputFormField";

/**
 * Delay in milliseconds to wait after a date picker closes before triggering the next action.
 * This ensures the picker's closing animation completes and the DOM is ready for the next interaction.
 */
const DATE_PICKER_TRANSITION_DELAY = 500;

export interface ReferenceFormProps {
  id: string;
  count: number;
  onSuccess?: () => void;
  scrollRef?: React.RefObject<HTMLDivElement>;
  initialFocusFirstField?: boolean;
}

export function ReferenceForm(props: ReferenceFormProps) {
  const { id: formId, count, onSuccess, scrollRef, initialFocusFirstField } = props;
  const { showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const startDateModalState = useModalState();
  const endDateModalState = useModalState();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, isValid, errors, touchedFields },
    setFocus,
  } = useForm<ReferenceFormData>({
    mode: "onTouched",
    resolver: zodResolver(referenceFormSchema),
    defaultValues: {
      [FORM_FIELDS.EMPLOYER_NAME]: "",
      [FORM_FIELDS.WORKER_TITLE]: "",
      [FORM_FIELDS.EMPLOYMENT_START_DATE]: undefined,
      [FORM_FIELDS.EMPLOYMENT_END_DATE]: undefined,
      [FORM_FIELDS.REFERENCE_FIRST_NAME]: "",
      [FORM_FIELDS.REFERENCE_LAST_NAME]: "",
      [FORM_FIELDS.REFERENCE_PHONE]: "",
      [FORM_FIELDS.REFERENCE_EMAIL]: "",
    },
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!initialFocusFirstField) {
      return;
    }
    setFocus(FORM_FIELDS.EMPLOYER_NAME);
  }, [initialFocusFirstField, setFocus]);

  const [employmentStartDate, employmentEndDate] = watch([
    FORM_FIELDS.EMPLOYMENT_START_DATE,
    FORM_FIELDS.EMPLOYMENT_END_DATE,
  ]);

  const handleStartDateConfirm = useCallback(
    (date: Date) => {
      setValue(FORM_FIELDS.EMPLOYMENT_START_DATE, date, {
        shouldValidate: true,
        shouldTouch: true,
      });
      startDateModalState.closeModal();
      setTimeout(() => {
        endDateModalState.openModal();
      }, DATE_PICKER_TRANSITION_DELAY);
    },
    [setValue, startDateModalState, endDateModalState]
  );

  const handleEndDateConfirm = useCallback(
    (date: Date) => {
      setValue(FORM_FIELDS.EMPLOYMENT_END_DATE, date, {
        shouldValidate: true,
        shouldTouch: true,
      });
      endDateModalState.closeModal();
      setTimeout(() => {
        setFocus(FORM_FIELDS.REFERENCE_FIRST_NAME);
      }, DATE_PICKER_TRANSITION_DELAY);
    },
    [setValue, endDateModalState, setFocus]
  );

  const { mutateAsync: createReferenceMutation } = useCreateProfessionalReference();
  async function onSubmit(formData: ReferenceFormData) {
    try {
      await createReferenceMutation(transformFormDataToRequest(formData), {
        onSuccess: async () => {
          logEvent(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCE_CREATED, {
            referenceCount: count,
          });
          showSuccessToast("Reference added successfully.");
        },
        onError: (error) => {
          const defaultErrorMessage = "Failed to create reference. Please try again.";
          const errorMessage = getErrorMessage(error);
          const userErrorMessage = errorMessage
            ? (KNOWN_CREATE_REFERENCE_USER_ERROR_MESSAGES[errorMessage] ?? defaultErrorMessage)
            : defaultErrorMessage;
          showErrorToast(userErrorMessage);
          logError(APP_V2_APP_EVENTS.CREATE_PROFESSIONAL_REFERENCE_ERROR, {
            error,
            metadata: { errorMessage },
          });
        },
      });
    } catch {
      return;
    }

    // We wait for invalidation to keep isSubmitting true until the list is updated.
    await invalidateProfessionalReferences(queryClient);
    reset();
    onSuccess?.();
  }
  return (
    <Box
      id={formId}
      component="form"
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      aria-label="Add Reference"
      onSubmit={handleSubmit(onSubmit)}
    >
      <PageContent ref={scrollRef} sx={{ gap: 5, paddingTop: 2 }}>
        <InputFormField
          formId={formId}
          name={FORM_FIELDS.EMPLOYER_NAME}
          control={control}
          label="Employer Name"
          placeholder="eg. Memorial Hospital"
          onEnterPress={() => setFocus(FORM_FIELDS.WORKER_TITLE)}
        />
        <InputFormField
          formId={formId}
          name={FORM_FIELDS.WORKER_TITLE}
          control={control}
          label="Your Title / Role"
          placeholder="eg. Registered Nurse"
          onEnterPress={() => startDateModalState.openModal()}
        />
        <EmploymentDateFields
          formId={formId}
          employmentStartDate={employmentStartDate}
          employmentEndDate={employmentEndDate}
          errors={errors}
          touchedFields={touchedFields}
          startDateModalState={startDateModalState}
          endDateModalState={endDateModalState}
          onStartDateConfirm={handleStartDateConfirm}
          onEndDateConfirm={handleEndDateConfirm}
        />
        <InputFormField
          formId={formId}
          name={FORM_FIELDS.REFERENCE_FIRST_NAME}
          control={control}
          label="Reference First Name"
          placeholder="eg. John"
          onEnterPress={() => setFocus(FORM_FIELDS.REFERENCE_LAST_NAME)}
        />
        <InputFormField
          formId={formId}
          name={FORM_FIELDS.REFERENCE_LAST_NAME}
          control={control}
          label="Reference Last Name"
          placeholder="eg. Smith"
          onEnterPress={() => setFocus(FORM_FIELDS.REFERENCE_PHONE)}
        />
        <InputFormField
          formId={formId}
          name={FORM_FIELDS.REFERENCE_PHONE}
          control={control}
          label="Reference Phone Number"
          placeholder="eg. 8186218860"
          type="tel"
          inputMode="tel"
          onEnterPress={() => setFocus(FORM_FIELDS.REFERENCE_EMAIL)}
        />
        <InputFormField
          formId={formId}
          name={FORM_FIELDS.REFERENCE_EMAIL}
          control={control}
          label="Reference Email"
          placeholder="eg. john.smith@example.com"
          type="email"
          inputMode="email"
          enterKeyHint="done"
        />
      </PageContent>
      <PageFooterWrapper>
        <LoadingButton
          fullWidth
          type="submit"
          variant="contained"
          isLoading={isSubmitting}
          disabled={!isValid}
          size="large"
        >
          Save Reference
        </LoadingButton>
      </PageFooterWrapper>
    </Box>
  );
}
